import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {Menu, X, Globe, ShoppingCart, User, ChevronDown} from 'lucide-react'
import { useCart } from '../contexts/CartContext'
import { lumi } from '../lib/lumi'

/**
 * HeaderV2 - Barista Map style navbar with two variants
 *
 * variant="transparent" (HomePageV2 only):
 *   - Default: transparent background, WHITE text/logo (overlays full-bleed Hero)
 *   - Scrolled (>80px): white background, BLACK text + subtle shadow
 *
 * variant="solid" (default — every other V2 page):
 *   - Always white background, BLACK text + subtle shadow
 *   - Hero/content starts BELOW navbar (no overlap)
 *
 * Container: max-w-[1600px] + px-6 md:px-10 (aligned with V2 sections)
 */
interface HeaderV2Props {
  variant?: 'transparent' | 'solid'
}

const HeaderV2: React.FC<HeaderV2Props> = ({ variant = 'solid' }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { itemCount } = useCart()
  const [scrolled, setScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [user, setUser] = useState(lumi.auth.user)

  const currentLang: 'zh' | 'en' = location.pathname.startsWith('/en') ? 'en' : 'zh'

  // Scroll listener for navbar style switch
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Auth state
  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange((authResult) => {
      setUser(authResult.user)
    })
    return () => unsubscribe()
  }, [])

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location])

  const nav = currentLang === 'zh'
    ? [
        { name: 'Home', href: `/${currentLang}/v2` },
        { name: 'Our Business', href: `/${currentLang}/business/v2` },
        { name: 'Stores', href: `/${currentLang}/store/v2` },
        { name: 'Shop', href: `/${currentLang}/shop/beans/v2` },
        { name: 'Wholesale', href: `/${currentLang}/wholesale/v2` },
        { name: 'Contact', href: `/${currentLang}/faq` },
      ]
    : [
        { name: 'Home', href: `/${currentLang}/v2` },
        { name: 'Our Business', href: `/${currentLang}/business/v2` },
        { name: 'Stores', href: `/${currentLang}/store/v2` },
        { name: 'Shop', href: `/${currentLang}/shop/beans/v2` },
        { name: 'Wholesale', href: `/${currentLang}/wholesale/v2` },
        { name: 'Contact', href: `/${currentLang}/faq` },
      ]

  const toggleLanguage = () => {
    const newLang = currentLang === 'zh' ? 'en' : 'zh'
    const currentPath = location.pathname.replace(/^\/(zh|en)/, '')
    const newPath = currentPath === '' || currentPath === '/' ? `/${newLang}` : `/${newLang}${currentPath}`
    navigate(newPath)
  }

  // Color tokens — solid variant is always dark; transparent flips on scroll
  const isDark = variant === 'solid' || scrolled
  const textColor = isDark ? 'text-black' : 'text-white'
  const hoverColor = isDark ? 'hover:text-black/60' : 'hover:text-white/70'
  const bgClass = isDark
    ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_0_0_rgba(0,0,0,0.06)]'
    : 'bg-transparent'

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${bgClass}`}
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="flex h-20 items-center justify-between">
            {/* Logo - left */}
            <Link to={`/${currentLang}/v2`} className="flex items-center gap-3">
              <div className={`transition-colors duration-300 ${textColor}`}>
                <div
                  className="text-lg md:text-xl tracking-[0.3em] leading-none"
                  style={{
                    fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
                    fontWeight: 400,
                  }}
                >
                  ONGU STUDIO
                </div>
                <div
                  className="text-[10px] md:text-xs tracking-[0.2em] mt-1 opacity-80"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  Coffee Roasters
                </div>
              </div>
            </Link>

            {/* Right side: nav + actions, all aligned right */}
            <div className="flex items-center gap-6 md:gap-8">
              {/* Desktop nav - right */}
              <nav className="hidden lg:flex items-center gap-8">
                {nav.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`text-[13px] tracking-[0.15em] transition-colors duration-200 ${textColor} ${hoverColor}`}
                    style={{
                      fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
                      fontWeight: 400,
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>

              {/* Right actions */}
              <div className="flex items-center gap-4 md:gap-5">
              {/* Language */}
              <button
                onClick={toggleLanguage}
                className={`hidden md:flex items-center gap-1 text-xs tracking-widest transition-colors duration-200 ${textColor} ${hoverColor}`}
                style={{ fontFamily: "'EB Garamond', serif" }}
                aria-label="Toggle language"
              >
                <Globe size={14} />
                <span>{currentLang.toUpperCase()}</span>
              </button>

              {/* Account */}
              <Link
                to={`/${currentLang}/member`}
                className={`hidden md:block transition-colors duration-200 ${textColor} ${hoverColor}`}
                aria-label="Account"
              >
                <User size={18} strokeWidth={1.5} />
              </Link>

              {/* Cart */}
              <Link
                to={`/${currentLang}/cart`}
                className={`relative transition-colors duration-200 ${textColor} ${hoverColor}`}
                aria-label="Cart"
              >
                <ShoppingCart size={18} strokeWidth={1.5} />
                {itemCount > 0 && (
                  <span
                    className={`absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center text-[10px] ${
                      isDark ? 'bg-black text-white' : 'bg-white text-black'
                    }`}
                  >
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </Link>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className={`lg:hidden transition-colors duration-200 ${textColor}`}
                aria-label="Open menu"
              >
                <Menu size={22} strokeWidth={1.5} />
              </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-[60] lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-[#FAF9F1] z-[70] lg:hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-black/10">
                <div
                  className="text-base tracking-[0.25em] text-black"
                  style={{ fontFamily: "'EB Garamond', 'Noto Serif TC', serif" }}
                >
                  ONGU STUDIO
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-black/70 hover:text-black"
                  aria-label="Close menu"
                >
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-6 py-8 space-y-1">
                {nav.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block py-3 text-lg tracking-[0.15em] text-black border-b border-black/5"
                    style={{ fontFamily: "'EB Garamond', 'Noto Serif TC', serif" }}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>

              <div className="px-6 py-5 border-t border-black/10 space-y-3">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 text-sm tracking-widest text-black/70"
                  style={{ fontFamily: "'EB Garamond', serif" }}
                >
                  <Globe size={14} />
                  <span>{currentLang === 'zh' ? 'JP / EN' : 'JP / EN'}</span>
                  <ChevronDown size={14} />
                </button>
                <Link
                  to={`/${currentLang}/member`}
                  className="flex items-center gap-2 text-sm tracking-widest text-black/70"
                >
                  <User size={14} />
                  <span style={{ fontFamily: "'EB Garamond', serif" }}>
                    {user ? (currentLang === 'zh' ? '會員中心' : 'Account') : (currentLang === 'zh' ? '登入' : 'Sign in')}
                  </span>
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

export default HeaderV2
